import React, { useEffect, useRef, useState } from "react";

const defaultCenter = { lat: 37.4239163, lng: -122.0947209 };

const isGoogleMapsReady = () =>
  typeof window !== "undefined" &&
  !!window.google?.maps &&
  typeof window.google.maps.importLibrary === "function";

const MapsView = ({ details, mapContainerStyle, interactive = true }) => {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [location, setLocation] = useState(defaultCenter);
  const [isLoaded, setIsLoaded] = useState(isGoogleMapsReady());
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (isLoaded) return;

    let cancelled = false;
    let retryCount = 0;
    const maxRetries = 150; // ~15s
    let timerId;

    const checkMapsLoaded = () => {
      if (cancelled) return;
      if (isGoogleMapsReady()) {
        setIsLoaded(true);
        setLoadError(false);
      } else if (retryCount < maxRetries) {
        retryCount++;
        timerId = setTimeout(checkMapsLoaded, 100);
      } else {
        setLoadError(true);
        console.error("Google Maps API failed to load");
      }
    };

    checkMapsLoaded();

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;

    if (details?.lat != null && details?.lng != null) {
      setLocation({ lat: details.lat, lng: details.lng });
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => console.error("Error getting location:", error),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  }, [isLoaded, details]);

  useEffect(() => {
    if (!isLoaded) return;

    let cancelled = false;

    async function initMap() {
      try {
        const { Map } = await window.google.maps.importLibrary("maps");
        const { AdvancedMarkerElement } = await window.google.maps.importLibrary(
          "marker"
        );

        if (cancelled || !mapRef.current) return;

        const map = new Map(mapRef.current, {
          center: location,
          zoom: 14,
          mapId: "4504f8b37365c3d0",
          disableDefaultUI: true,
          zoomControl: interactive,
          fullscreenControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          rotateControl: false,
          scaleControl: false,
          keyboardShortcuts: false,
          clickableIcons: false,
          gestureHandling: interactive ? "auto" : "none",
        });

        if (markerRef.current) {
          markerRef.current.map = null;
        }

        markerRef.current = new AdvancedMarkerElement({
          map,
          position: location,
          title: "Selected Location",
        });
      } catch (error) {
        console.error("Error initializing map:", error);
      }
    }

    initMap();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, location, interactive]);

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps...</div>;

  return <div ref={mapRef} style={mapContainerStyle} />;
};

export default MapsView;
